import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { DesktopOnly } from "@/components/ui";
import { Card, DatePickerInput, Group, Input, Loader, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { formatIso, parseIso } from "@/features/datos-generales/utils/iso-date";
import type { MiSolicitud } from "@/lib/types";
import {
  filtrarSolicitudes,
  ordenarSolicitudes,
  siguienteSort,
  type SolicitudFiltros,
  type SolicitudSort,
  type SolicitudSortKey,
} from "../utils/ordenar-solicitudes";
import { SolicitudRow } from "./SolicitudRow";

/**
 * Columnas consolidadas (revisión UX): Proveedor absorbe el RFC (2ª línea),
 * Total absorbe la Moneda (código ISO inline · regla de `AGENTS.md` para
 * vistas multi-moneda) y Núm. Doc se retira (HOTSPOT-DOMINIO sin fuente de
 * dato · era una columna permanentemente vacía). El espacio liberado va a
 * Descripción, la columna que el usuario lee para reconocer una solicitud.
 */
function columnas(): { key: SolicitudSortKey | null; label: string; align?: "right" | "center"; w: number; filtro?: "fecha" | "proveedor" }[] {
  return [
    // Pesos RELATIVOS (se normalizan a %) · el folio son 15 dígitos (~148px) · un
    // token que NO parte, así que conserva la mayor proporción para no cortarse.
    // La columna Moneda se fusionó en Total (código ISO como 2ª línea) · una
    // columna menos para que la tabla quepa SIN scroll horizontal.
    { key: "folio", label: "Folio", w: 210 },
    { key: "estado", label: "Estado", w: 130 },
    { key: "tipo", label: "Tipo", w: 110 },
    { key: "organizacion", label: "Organización", w: 130 },
    { key: "area", label: "Área", w: 100 },
    { key: "proveedor", label: "Proveedor", w: 155, filtro: "proveedor" },
    { key: "descripcion", label: "Descripción", w: 180 },
    { key: "total", label: "Total", align: "right", w: 130 },
    // Anchos con margen para que el placeholder del filtro de fecha
    // ("Fecha pago…" / "Creación…" + ícono de calendario) quepa en una línea.
    { key: "fechaPago", label: "Fecha pago", w: 105, filtro: "fecha" },
    { key: "creacion", label: "Creación", w: 100, filtro: "fecha" },
    { key: null, label: "", align: "right", w: 75 },
  ];
}

export function SolicitudesTable({
  solicitudes,
  filtros,
  sort,
  buscando = false,
  mostrarFiltros = true,
  onFiltrar,
  onOrdenar,
}: {
  solicitudes: MiSolicitud[];
  filtros: SolicitudFiltros;
  sort: SolicitudSort | null;
  buscando?: boolean;
  /** Fila de filtros por columna colapsable (revisión UX) · el toggle vive en la toolbar de la página. */
  mostrarFiltros?: boolean;
  onFiltrar: (key: SolicitudSortKey, valor: string) => void;
  onOrdenar: (sort: SolicitudSort) => void;
}) {
  const COLS = useMemo(() => {
    const cols = columnas();
    const totalPeso = cols.reduce((acc, c) => acc + c.w, 0);
    return cols.map((c) => ({ ...c, pct: `${((c.w / totalPeso) * 100).toFixed(2)}%` }));
  }, []);

  const visibles = useMemo(() => ordenarSolicitudes(filtrarSolicitudes(solicitudes, filtros), sort), [solicitudes, filtros, sort]);

  const alOrdenar = (key: SolicitudSortKey) => onOrdenar(siguienteSort(sort, key));

  // Scrollbar horizontal DUPLICADO arriba de la tabla (el nativo queda abajo y
  // obliga a bajar para encontrarlo). Una barra superior espejo, sincronizada
  // en ambos sentidos con el contenedor real de la tabla.
  const tablaScrollRef = useRef<HTMLDivElement>(null);
  const barraSuperiorRef = useRef<HTMLDivElement>(null);
  const [anchoContenido, setAnchoContenido] = useState(0);

  useEffect(() => {
    const el = tablaScrollRef.current;
    if (!el) return;
    const medir = () => setAnchoContenido(el.scrollWidth);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visibles.length, mostrarFiltros]);

  // Sincronía sin flag: fijar scrollLeft al mismo valor no re-dispara scroll,
  // así que el eco converge en un paso (no hay bucle infinito).
  const alScrollBarra = () => {
    if (tablaScrollRef.current && barraSuperiorRef.current)
      tablaScrollRef.current.scrollLeft = barraSuperiorRef.current.scrollLeft;
  };
  const alScrollTabla = () => {
    if (tablaScrollRef.current && barraSuperiorRef.current)
      barraSuperiorRef.current.scrollLeft = tablaScrollRef.current.scrollLeft;
  };

  return (
    <DesktopOnly>
      <Card padding="xs" className="relative p-0">
        {buscando && (
          <Group gap={6} className="pointer-events-none absolute right-3 top-2 z-20">
            <Loader size="xs" color="multiva" />
            <Text size="xs" c="dimmed" fw={500}>
              Buscando…
            </Text>
          </Group>
        )}
        {/* La tabla es `layout: fixed` y ocupa el 100% del contenedor: en
            escritorio (≥1024px de contenido) las columnas se reparten por su
            proporción y el texto TRUNCA con tooltip · NO hay scroll horizontal.
            El `minWidth` bajó a 1024 (antes 1450 · forzaba scroll en pantallas
            de contenido más angosto que eso): sólo por debajo de 1024 la tabla
            scrollea DENTRO de su card para no encimar el folio de 15 dígitos.
            Trade-off aceptado: el header no es sticky (la lista pagina a 15
            filas · poco scroll vertical). */}
        {/* Barra de scroll horizontal ESPEJO arriba de la tabla · su contenido
            interno replica el ancho real de la tabla para que el pulgar mida lo
            mismo; se sincroniza en ambos sentidos con el contenedor de abajo. */}
        <div
          ref={barraSuperiorRef}
          onScroll={alScrollBarra}
          className="overflow-x-auto overflow-y-hidden"
          aria-hidden="true"
        >
          <div style={{ width: anchoContenido }} className="h-px" />
        </div>
        <div ref={tablaScrollRef} onScroll={alScrollTabla} className="overflow-x-auto">
        <Table layout="fixed" minWidth={1024} highlightOnHover verticalSpacing="lg" horizontalSpacing="sm" className="uppercase">
          {/* Encabezado con fondo gris tenue (`ink-100`) para el contraste del
              diseño de referencia. Ya NO es sticky: dentro del contenedor con
              scroll horizontal, el `position: sticky` lo despegaba y dejaba que
              las filas se colaran por detrás (header desalineado). */}
          <Thead className="bg-ink-100 dark:bg-ink-800">
            <Tr>
              {COLS.map((c, i) =>
                c.key ? (
                  <SortableTh key={c.key} sortKey={c.key} sort={sort} onSort={alOrdenar} align={c.align === "right" ? "right" : "left"} w={c.pct}>
                    {c.label}
                  </SortableTh>
                ) : (
                  <Th key={`x-${i}`} align={c.align} style={{ width: c.pct }}>
                    <Text size="xs" fw={600} className="whitespace-normal break-words leading-tight">
                      {c.label}
                    </Text>
                  </Th>
                ),
              )}
            </Tr>
            {mostrarFiltros && (
            // Franja diferenciada (opción A elegida por el usuario): tinte
            // tenue RELATIVO al texto (foreground/5) — `muted` en claro es
            // #f8fafc y sobre la card blanca resultaba invisible; foreground/5
            // oscurece sutil en claro y aclara sutil en oscuro. Los inputs
            // (bg-background) ganan cuerpo por contraste. Sin lupa repetida:
            // en una fila entera de filtros, el placeholder ya lo dice todo.
            <Tr className="border-t border-border bg-foreground/10">
              {COLS.map((c, i) => {
                if (!c.key) return <Th key={`xf-${i}`} className="!px-1 !py-1.5" />;
                const key = c.key;
                return (
                  <Th key={key} className="!px-1 !py-1.5 align-top">
                    {c.filtro === "fecha" ? (
                      <DatePickerInput
                        size="xs"
                        clearable
                        placeholder={`${c.label}…`}
                        value={parseIso(filtros[key])}
                        onChange={(d) => onFiltrar(key, formatIso(d))}
                        wrapperClassName="normal-case"
                        placeholderClassName="truncate text-muted-foreground/60"
                      />
                    ) : c.filtro === "proveedor" ? (
                      // Input único inteligente (opción A elegida por el
                      // usuario · los dos inputs apilados rompían la armonía
                      // de la fila): si lo tecleado tiene forma de RFC
                      // (3-4 letras seguidas de dígito · un RFC parcial como
                      // "TVI140" ya matchea, y el server busca por contains)
                      // filtra por `rfcProveedor`; cualquier otro texto, por
                      // razón social. Se limpia la llave contraria para no
                      // dejar un AND fantasma al cambiar de forma.
                      <Input
                        size="xs"
                        placeholder="Proveedor o RFC…"
                        value={filtros.rfcProveedor || filtros.proveedor || ""}
                        onChange={(e) => {
                          const v = e.currentTarget.value;
                          const esRfc = /^[A-ZÑ&]{3,4}\d/i.test(v.trim());
                          onFiltrar(esRfc ? "rfcProveedor" : "proveedor", v);
                          onFiltrar(esRfc ? "proveedor" : "rfcProveedor", "");
                        }}
                        aria-label="Filtrar por proveedor (razón social o RFC)"
                        // Placeholder aclarado (60%): el gris pleno pesaba
                        // demasiado con la fila completa de filtros vacíos.
                        className="normal-case placeholder:text-muted-foreground/60"
                      />
                    ) : (
                      <Input
                        size="xs"
                        // Placeholder descriptivo por columna ("Folio…",
                        // "Estado…"…) en vez de ocho "Buscar…" idénticos que
                        // no decían qué busca cada uno.
                        placeholder={`${c.label}…`}
                        value={filtros[key] ?? ""}
                        onChange={(e) => onFiltrar(key, e.currentTarget.value)}
                        aria-label={`Filtrar por ${c.label}`}
                        className="normal-case placeholder:text-muted-foreground/60"
                      />
                    )}
                  </Th>
                );
              })}
            </Tr>
            )}
          </Thead>
          <Tbody className="transition-opacity duration-120" style={{ opacity: buscando ? 0.5 : 1 }}>
            {visibles.length === 0 ? (
              <Tr>
                <Td colSpan={COLS.length}>
                  <Text size="sm" c="dimmed" ta="center" className="py-3">
                    Sin resultados para los filtros.
                  </Text>
                </Td>
              </Tr>
            ) : (
              visibles.map((s) => <SolicitudRow key={s.id} s={s} />)
            )}
          </Tbody>
        </Table>
        </div>
      </Card>
    </DesktopOnly>
  );
}

function SortableTh({
  children,
  sortKey,
  sort,
  onSort,
  align = "left",
  w,
}: {
  children: ReactNode;
  sortKey: SolicitudSortKey;
  sort: SolicitudSort | null;
  onSort: (key: SolicitudSortKey) => void;
  align?: "left" | "right";
  w?: string | number;
}) {
  const activa = sort?.key === sortKey;
  const Icono = activa ? (sort!.dir === "asc" ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <Th className="!p-0 align-top" style={{ width: w }}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Ordenar por ${typeof children === "string" ? children : sortKey}`}
        className={`flex w-full items-center gap-1 px-2.5 py-2 ${align === "right" ? "justify-end" : "justify-start"}`}
      >
        <Text size="xs" fw={600} className="whitespace-normal break-words uppercase leading-tight text-ink-700 dark:text-ink-200">
          {children}
        </Text>
        <Icono size={15} stroke={2} className={`shrink-0 ${activa ? "text-multiva-600" : "text-ink-600 dark:text-ink-300"}`} />
      </button>
    </Th>
  );
}
